import { HttpClient, HttpParams, HttpRequest } from '@angular/common/http';
import { forwardRef, Inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { CONFIG } from '../common/config';
import { debugOffline, Permissions } from '../common/constants';
import { ACTION_STATUS_ATTACHMENTS, TICKET_ATTACHMENTS, TICKET_NOTES, USER_ACTIONS_V2, USER_TEMPLATES_V2, WORK_ORDERS_V2 } from '../common/endpoints';
import { LOCALLY_HOSTED } from '../common/setup';
import { ActionAttachmentType, ActionStatus, OFFLINE_STORES, OfflineRequest, OfflineStore, OfflineStoreCrudApi, PagedList, RequestMethod, Thing, TicketItemType } from '../model';
import { OfflineRequestSynchronizerDialogComponent } from '../shared/component/offline-request-synchronizer-dialog/offline-request-synchronizer-dialog.component';
import { ActionDefinitionTypeService } from './action-definition-type.service';
import { ActionDefinitionService } from './action-definition.service';
import { ActionDiscardReasonService } from './action-discard-reason.service';
import { ActionIssueTypeService } from './action-issue-type.service';
import { AuthenticationService } from './authentication.service';
import { IndexedDBService } from './indexeddb.service';
import { ProductModelService } from './product-model.service';
import { ThingContextService } from './thing-context.service';
import { ThingDefinitionService } from './thing-definition.service';
import { TicketService } from './ticket.service';
import { UiService } from './ui.service';
import { UserActionStatusService } from './user-action-status.service';
import { UserCustomerService } from './user-customer.service';
import { UserLocationService } from './user-location.service';
import { UserThingService } from './user-thing.service';

@Injectable()
export class OfflineNavigationService {

    private SKIP_INTERCEPTOR_PARAMS = new HttpParams().set('skipInterceptor', true);
    private storedThingIds: string[] = [];
    private OFFLINE_STORE_MAP_BY_NAME: Map<string, OfflineStore> = new Map<string, OfflineStore>();
    private OFFLINE_STORE_MAP_BY_GET_API: Map<string, OfflineStore> = new Map<string, OfflineStore>();
    private OFFLINE_STORE_MAP_BY_GET_BY_ID_API: Map<string, OfflineStore> = new Map<string, OfflineStore>();
    private OFFLINE_STORE_MAP_BY_GET_PAGED_API: Map<string, OfflineStore> = new Map<string, OfflineStore>();
    private OFFLINE_ID_PREFIX = 'OFFLINE_ID_';
    private counter = Math.floor(Math.random() * 0xffffff);
    private random = crypto.getRandomValues(new Uint8Array(5));
    private synchDialog: MatDialogRef<OfflineRequestSynchronizerDialogComponent>;
    private syncPromise: Promise<void>;

    constructor(
        @Inject(forwardRef(() => IndexedDBService)) private indexedDBService: IndexedDBService,
        @Inject(forwardRef(() => UiService)) private uiService: UiService,
        @Inject(forwardRef(() => ThingDefinitionService)) private thingDefinitionService: ThingDefinitionService,
        @Inject(forwardRef(() => ProductModelService)) private productModelService: ProductModelService,
        @Inject(forwardRef(() => UserCustomerService)) private userCustomerService: UserCustomerService,
        @Inject(forwardRef(() => UserLocationService)) private userLocationService: UserLocationService,
        @Inject(forwardRef(() => UserThingService)) private userThingService: UserThingService,
        @Inject(forwardRef(() => ActionDefinitionService)) private actionDefinitionService: ActionDefinitionService,
        @Inject(forwardRef(() => UserActionStatusService)) private userActionStatusService: UserActionStatusService,
        @Inject(forwardRef(() => HttpClient)) private httpClient: HttpClient,
        @Inject(forwardRef(() => MatDialog)) protected dialog: MatDialog,
        @Inject(forwardRef(() => ThingContextService)) protected thingContextService: ThingContextService,
        @Inject(forwardRef(() => ActionDefinitionTypeService)) protected actionDefinitionTypeService: ActionDefinitionTypeService,
        @Inject(forwardRef(() => ActionDiscardReasonService)) protected actionDiscardReasonService: ActionDiscardReasonService,
        @Inject(forwardRef(() => ActionIssueTypeService)) protected actionIssueTypeService: ActionIssueTypeService,
        @Inject(forwardRef(() => AuthenticationService)) protected authenticationService: AuthenticationService,
        @Inject(forwardRef(() => TicketService)) protected ticketService: TicketService
    ) {
        OFFLINE_STORES.forEach(s => {
            this.OFFLINE_STORE_MAP_BY_NAME.set(s.name, s);
            if (s.getApi) {
                this.OFFLINE_STORE_MAP_BY_GET_API.set(s.getApi, s);
            }
            if (s.getByIdApi) {
                this.OFFLINE_STORE_MAP_BY_GET_BY_ID_API.set(s.getByIdApi, s);
            }
            if (s.getPagedApi) {
                this.OFFLINE_STORE_MAP_BY_GET_PAGED_API.set(s.getPagedApi, s);
            }
        });

        // triggers the synchronization when coming back online
        window.addEventListener('online', () => {
            if (LOCALLY_HOSTED) {
                this.synchronizeOfflineRequests().then(() => {
                    this.storeGeneralData();
                    const thing = this.thingContextService.getCurrentThing();
                    if (thing) {
                        this.storeThingData(thing);
                    }
                });
            }
        });
    }

    synchronizeOfflineRequests(): Promise<void> {
        debugOffline('Start synchronizing offline requests');
        this.syncPromise = this.indexedDBService.findWithKeys(IndexedDBService.OFFLINE_REQUEST_STORE_NAME).then(entries => {
            if (!entries?.length) {
                debugOffline('No request to synchronize');
                return Promise.resolve();
            }
            debugOffline(`${entries.length} requests to synchronize`);
            this.initSynchDialog(entries.length);
            return this.resolveRequests(entries, 0).then(() => {
                debugOffline('Synchronization completed');
                this.synchDialog.close();
            });
        });
        return this.syncPromise;
    }

    private initSynchDialog(total: number): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = true;
        dialogConfig.data = { counter: 0, total: total };
        this.synchDialog = this.dialog.open(OfflineRequestSynchronizerDialogComponent, dialogConfig);
    }

    private resolveRequests(offlineRequestEntries: { key: string, value: OfflineRequest }[], index: number): Promise<void> {
        debugOffline(`Resolving request with index ${index}`);
        let offlineReq = offlineRequestEntries[index];
        const httpRequest = this.getHttpRequest(offlineReq.value).catch(err => {
            console.error(err);
            return { error: true };
        });
        return httpRequest.then(resp => {
            let deleteRequest = Promise.resolve();
            if (!resp?.error) {
                debugOffline('Request completed. Deleting db entry');
                deleteRequest = this.indexedDBService.clearById(IndexedDBService.OFFLINE_REQUEST_STORE_NAME, offlineReq.key);
            }
            this.synchDialog.componentInstance.increaseCounter();
            if (offlineRequestEntries.length > index + 1) {
                return deleteRequest.then(() => this.resolveRequests(offlineRequestEntries, index + 1));
            }
            return deleteRequest;
        });
    }

    private getHttpRequest(offlineReq: OfflineRequest): Promise<any> {
        const params = offlineReq.params || {};
        params['skipInterceptor'] = ['true'];
        switch (offlineReq.method) {
            case RequestMethod.POST:
                let body;
                if (offlineReq.file) {
                    body = new FormData();
                    body.set('file', offlineReq.file, offlineReq.file.name);
                } else {
                    body = offlineReq.body;
                }
                return firstValueFrom(this.httpClient.post(offlineReq.url, body, { headers: offlineReq.headers, params: params }));
            case RequestMethod.PATCH:
                return firstValueFrom(this.httpClient.patch(offlineReq.url, offlineReq.body, { headers: offlineReq.headers, params: params }));;
            case RequestMethod.DELETE:
                return firstValueFrom(this.httpClient.delete(offlineReq.url, { headers: offlineReq.headers, params: params }));;
            default:
                throw 'Invalid offline request method ' + offlineReq.method;
        }
    }

    storeGeneralData(): void {
        this.storeTemplates();
        this.storeThingDefinitions();
        this.storeProductModels();
        this.storeCustomers();
        this.storeLocations();
        this.storeThings();
        this.storeActionConfigData();
        this.storeTicketTypes();
    }

    storeThingData(thing: Thing): void {
        this.syncPromise.then(() => { // awaits for synch if loading
            if (!this.storedThingIds.includes(thing.id)) {
                this.storeTemplates(thing.thingDefinitionId);
                this.storeActionDefinitions(thing.thingDefinitionId);
                this.storedThingIds.push(thing.id);
            }
            this.storeActions(thing.id);
            this.storeHistoricalActions(thing.id);
            this.storeWorkOrders(thing.id);
            this.storeHistoricalWorkOrders(thing.id);
            this.storeTickets(thing.id);
            this.updateThingData(thing.id);
        })
    }

    private storeTemplates(thingDefinitionId?: string): void {
        this.uiService.getRecursivelyAllUserTemplates().then(allTemplates => {
            allTemplates.forEach(template => {
                const templateName = template.name;
                const thingDefParam = thingDefinitionId ? `&thingDefinitionId=${thingDefinitionId}` : '';
                const url = `${CONFIG.BASE_URL}${USER_TEMPLATES_V2}?name=${templateName}&page=0&size=1${thingDefParam}`;
                const data = { "content": [template] };
                this.indexedDBService.save(url, data);

                // triggers the content call to save it in storage (and cache)
                const key = this.uiService.getTemplateContentKey(templateName, thingDefinitionId);
                this.uiService.getTemplateContentAndSaveInCache(template.id, key);
            });
        }).catch(err => console.error(err));
    }

    private storeThingDefinitions(): void {
        this.thingDefinitionService.getThingDefinitions()
            .then(thingDefs => this.storeResource(thingDefs, 'thingDefinition')).catch(err => console.error(err));
    }

    private storeResource(resources: any[], storeName: string, idxKey?: string) {
        debugOffline(`Saving in store '${storeName}' with key '${idxKey}' resources: ${JSON.stringify(resources)}`);
        this.clearStoreWithRetry(storeName, idxKey).then(() =>
            resources.forEach(t => this.doSaveResource(storeName, t))
        );
    }

    private storeProductModels(): void {
        this.productModelService.getRecursivelyAllProductModels(null, null, this.SKIP_INTERCEPTOR_PARAMS)
            .then(productModels => this.storeResource(productModels, 'productModel')).catch(err => console.error(err));
    }

    private doSaveResource(storeName: string, data: any): Promise<void> {
        return this.indexedDBService.saveResource(storeName, data);
    }

    private clearStoreWithRetry(storeName: string, idxKey?: string): Promise<void> {
        const storeObj = this.OFFLINE_STORE_MAP_BY_NAME.get(storeName);

        const tryClear = () => {
            if (storeObj.indexField && idxKey) {
                return this.indexedDBService
                    .clearByIndex(storeName, storeObj.indexField, idxKey)
                    .catch(err => console.error(err));
            } else {
                return this.indexedDBService
                    .clearStore(storeName)
                    .catch(err => console.error(err));
            }
        };

        return tryClear().catch(err => {
            if (err === IndexedDBService.DB_NOT_REFRENCED_ERROR) {
                return this.indexedDBService.init().then(() => {
                    return tryClear();
                }).catch(err => {
                    console.error(err + '. Trying to clear ' + storeName);
                    throw err;
                });
            } else {
                console.error(err);
                throw err;
            }
        });
    }

    private storeCustomers(): void {
        if (this.authenticationService.isPartnerUser() || this.authenticationService.isOrganizationUser()) {
            this.userCustomerService.getRecursivelyAllCustomers(null, null, this.SKIP_INTERCEPTOR_PARAMS)
                .then(customers => this.storeResource(customers, 'customer')).catch(err => console.error(err));
        }
    }

    private storeLocations(): void {
        this.userLocationService.getRecursivelyAllLocations(null, null, null, this.SKIP_INTERCEPTOR_PARAMS)
            .then(locations => this.storeResource(locations, 'location')).catch(err => console.error(err));
    }

    private storeActionConfigData(): void {

        // triggers the requests to save it in storage
        this.actionDefinitionTypeService.getAllActionDefinitionTypes();
        this.actionDiscardReasonService.getAllActionDiscardReasons();
        this.actionIssueTypeService.getAllIssueTypes();
    }

    private storeTicketTypes(): void {
        if (this.authenticationService.hasPermission(Permissions.READ_TICKET)) {

            // triggers the requests to save it in storage
            this.ticketService.getTicketTypes();
        }
    }

    private storeThings(): void {
        this.userThingService.getRecursivelyAllThings(null, null, null, null, null, this.SKIP_INTERCEPTOR_PARAMS)
            .then(things => this.storeResource(things, 'thing')).catch(err => console.error(err));
    }

    private storeActionDefinitions(thingDefinitionId: string): void {
        const params = new HttpParams().set('thingDefinitionId', thingDefinitionId);
        this.actionDefinitionService.getRecursivelyAll(null, null, params)
            .then(actionDefinitions => this.storeResource(actionDefinitions, 'actionDefinition', thingDefinitionId))
    }

    private storeActions(thingId: string): void {
        const params = new HttpParams().set('thingId', thingId).set('active', true);
        this.userActionStatusService.getRecursivelyAllStatuses(params)
            .then(actions => this.storeResource(actions, 'action', thingId)).catch(err => console.error(err));
    }

    private storeTickets(thingId: string): void {
        if (this.authenticationService.hasPermission(Permissions.READ_TICKET)) {
            this.ticketService.getRecursivelyAllTickets(false, thingId)
                .then(tickets => this.storeResource(tickets, 'ticket', thingId)).catch(err => console.error(err));
        }
    }

    private storeHistoricalActions(thingId: string): void {
        const params = new HttpParams().set('thingId', thingId).set('active', false);
        this.userActionStatusService.getRecursivelyAllStatuses(params)
            .then(actions => this.storeResource(actions, 'historicalAction', thingId)).catch(err => console.error(err));
    }

    private storeWorkOrders(thingId: string): void {
        if (this.authenticationService.hasPermission(Permissions.READ_WORK_ORDER)) {
            const params = new HttpParams().set('thingId', thingId).set('active', true);
            this.userActionStatusService.getRecursivelyAllWorkOrders(params)
                .then(workOrders => this.storeResource(workOrders, 'workOrder', thingId)).catch(err => console.error(err));
        }
    }

    private storeHistoricalWorkOrders(thingId: string): void {
        if (this.authenticationService.hasPermission(Permissions.READ_WORK_ORDER)) {
            const params = new HttpParams().set('thingId', thingId).set('active', false);
            this.userActionStatusService.getRecursivelyAllWorkOrders(params)
                .then(workOrders => this.storeResource(workOrders, 'historicalWorkOrder', thingId)).catch(err => console.error(err));
        }
    }

    private updateThingData(id: string): void {
        this.indexedDBService.saveResource(IndexedDBService.THING_DATA_STORE_NAME, { id: id, timestamp: new Date().getTime() });
    }

    getStoreFromApi(req: HttpRequest<any>): OfflineStore {
        return this.anyApiEndsWith(this.OFFLINE_STORE_MAP_BY_GET_API, req) || this.anyApiEndsWith(this.OFFLINE_STORE_MAP_BY_GET_PAGED_API, req) || this.anyApiRegexMatchById(req);
    }

    private anyApiEndsWith(map: Map<string, OfflineStore>, req: HttpRequest<any>): OfflineStore {
        const key = Array.from(map.keys()).find(key => req.url.endsWith(key));
        return map.get(key);
    }

    private anyApiRegexMatchById(req: HttpRequest<any>): OfflineStore {
        const key = Array.from(this.OFFLINE_STORE_MAP_BY_GET_BY_ID_API.keys()).find(key => this.matchesApiUrl(req.url, key));
        return this.OFFLINE_STORE_MAP_BY_GET_BY_ID_API.get(key);
    }

    private matchesApiUrl(url: string, api: string): boolean {
        const pattern = api.replace("{id}", "([a-zA-Z0-9_]+)");
        const regex = new RegExp(`${pattern}$`);
        return regex.test(url);
    }

    saveWithRetry(urlWithParams: string, data: any): void {
        const trySave = () => this.indexedDBService.save(urlWithParams, data);

        trySave().catch(err => {
            if (err === IndexedDBService.DB_NOT_REFRENCED_ERROR) {
                this.indexedDBService.init()
                    .then(() => trySave())
                    .catch(err => {
                        console.error(err + '. Trying to save ' + urlWithParams);
                    });
            } else {
                console.error(err);
            }
        });
    }


    getWithRetry(req: HttpRequest<any>): Promise<any> {
        let store = this.getStoreFromApi(req);
        const tryGet = () => {
            if (store) {
                return this.computeComplexFindRequest(store, req);
            } else {
                return this.indexedDBService.findByUrl(req.urlWithParams);
            }
        };

        return tryGet().catch(err => {
            if (err === IndexedDBService.DB_NOT_REFRENCED_ERROR) {
                return this.indexedDBService.init().then(() => {
                    return tryGet();
                });
            } else {
                console.error(err);
                throw err;
            }
        });
    }

    private computeComplexFindRequest(store: OfflineStore, req: HttpRequest<any>): Promise<any> {
        if (store.getApi && req.url.endsWith(store.getApi)) { // ARRAY
            return this.indexedDBService.findAll(store.name);
        } else if (store.getByIdApi && this.matchesApiUrl(req.url, store.getByIdApi)) {  // SINGLE ITEM BY ID
            const id = this.extractIdFromUrl(req.url, store.getByIdApi);
            return this.indexedDBService.findByUniqueKey(store.name, id);
        } else { // PAGED RESULT
            const params = req.params;
            store = this.disambiguateApi(req.url, params, store);
            const indexField = store.indexField;
            const indexValue = indexField ? params.get(indexField) : null;
            return (indexValue ? this.indexedDBService.findByIndex(store.name, indexField, indexValue) : this.indexedDBService.findAll(store.name))
                .then(objects => this.mapObjectsToPage(objects, store, params));
        }
    }

    private extractIdFromUrl(url: string, api: string): string | null {
        const pattern = api.replace("{id}", "([a-zA-Z0-9_]+)");
        const regex = new RegExp(`${pattern}$`);
        const match = url.match(regex);
        return match ? match[1] : null;
    }

    // these stores has same api, must be disambiguate
    private disambiguateApi(url: string, params: HttpParams, store: OfflineStore): OfflineStore {
        if (url.endsWith(USER_ACTIONS_V2)) {
            return this.OFFLINE_STORE_MAP_BY_NAME.get(params.get('active') == 'true' ? 'action' : 'historicalAction');
        } else if (url.endsWith(WORK_ORDERS_V2)) {
            return this.OFFLINE_STORE_MAP_BY_NAME.get(params.get('active') == 'true' ? 'workOrder' : 'historicalWorkOrder');
        }
        return store;
    }

    private mapObjectsToPage(objects: any[], store: OfflineStore, params: HttpParams): PagedList<any> {
        objects = this.filterByCustomFields(objects, store.filterFields, params);
        objects = this.filterBySearchText(objects, params);
        return this.sortAndSlice(objects, params);
    }

    private filterByCustomFields(objects: any[], filterFields: string[], params: HttpParams): any[] {
        if (!filterFields?.length) {
            return objects;
        }
        let filterValues: { field: string, value: string }[] = [];
        for (let filterField of filterFields) {
            if (params.has(filterField)) {
                filterValues.push({ field: filterField, value: params.get(filterField) });
            }
        }
        if (!filterValues.length) {
            return objects;
        }
        return objects.filter(o => this.applyFilters(o, filterValues));
    }

    private applyFilters(object: any, filterValues: { field: string, value: string }[]): boolean {
        return filterValues.every(f => {
            const filter = this.getFilter(f.value);
            return filter(object[f.field]);
        });
    }

    private getFilter(value: string): (filter: string) => boolean {
        const OPERATORS = { 'eq': (a: any, b: any) => a == b };
        const operationParts = value.split(';');
        let operator = OPERATORS['eq'];
        let val = value;
        if (operationParts.length > 1) {
            operator = OPERATORS[operationParts[0]] || OPERATORS['eq'];
            val = operationParts[1];
        }
        return (filter) => operator(filter, val);
    }

    private filterBySearchText(objects: object[], params: HttpParams): object[] {
        const searchText = params.get('searchText');
        const searchFields = params.getAll('searchField');
        if (!searchText || !searchFields?.length) {
            return objects;
        }
        const normalizedSearch = (searchText as any).replaceAll('*', '').toLowerCase();
        return objects.filter(obj =>
            searchFields.some(field => {
                const value = this.getNestedValue(obj, field);
                if (value == null) {
                    return false
                };
                return String(value).toLowerCase().includes(normalizedSearch);
            })
        );
    }

    private getNestedValue(obj: any, path: string): any {
        return path.split('.').reduce((current, key) => current?.[key], obj);
    }

    private sortAndSlice(objects: any[], params: HttpParams): PagedList<any> {
        // sort
        let sortField = 'id';
        let sortDirection = 'asc';
        const sortFilter = params.get('sort');
        if (sortFilter) {
            const sortParts = sortFilter.split(',');
            sortField = sortParts[0];
            sortDirection = sortParts[1];
        }
        objects.sort(this.sort(sortField, sortDirection));

        // slice
        let page = Number(params.get('page') || 0);
        if (page < 0) {
            page = 0;
        }
        const size = Number(params.get('size') || 50);
        const offset = page * size;
        const slicedContent = objects.slice(offset, offset + size);
        const totalPages = Math.ceil(objects.length / size);
        return {
            content: slicedContent,
            number: page,
            size: size,
            totalElements: slicedContent.length,
            last: page >= (totalPages - 1),
            totalPages: totalPages,
            first: page == 0,
            numberOfElements: objects.length
        }
    }

    private sort(sortField: string, sortDirection: string): (a: any, b: any) => number {
        return (o1, o2) => {
            const valueA = this.getNestedValue(o1, sortField);
            const valueB = this.getNestedValue(o2, sortField);

            // null objs
            if (valueA == null && valueB == null) return 0;
            if (valueA == null) return sortDirection === 'asc' ? -1 : 1;
            if (valueB == null) return sortDirection === 'asc' ? 1 : -1;

            // strings
            if (typeof valueA === 'string' && typeof valueB === 'string') {
                const comparison = valueA.localeCompare(valueB);
                return sortDirection === 'asc' ? comparison : -comparison;
            }

            // numbers
            if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
            if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;

            return 0;
        };
    }

    getStoreByWriteApi(req: HttpRequest<any>): { store: OfflineStore, crudApi: OfflineStoreCrudApi } {
        for (let store of OFFLINE_STORES) {
            const crudApi = store.crudApis?.find(a => a.method == req.method && this.matchesApiUrl(req.url, a.apiUrl));
            if (crudApi) {
                return { store, crudApi };
            }
        }
        return null;
    }

    saveRequest(req: HttpRequest<any>, storeName: string, crudApi: OfflineStoreCrudApi): Promise<any> {
        const headersMap = this.getHeadersMap(req);
        const paramsMap = this.getParamsMap(req);
        const body = this.getBody(req, crudApi);
        const offlineReq: OfflineRequest = { storeName: storeName, url: req.url, params: paramsMap, body: body, method: RequestMethod[req.method], headers: headersMap };
        let resourceUpdateRequest: Promise<any>;
        let offlineSaveRequest: Promise<any>;
        if (crudApi.method == RequestMethod.PATCH) {
            const id = this.extractIdFromUrl(req.url, crudApi.apiUrl);
            resourceUpdateRequest = this.indexedDBService.findByUniqueKey(storeName, id).then(obj => {
                const patchedBody = Object.assign(obj, body)
                return this.doSaveResource(storeName, patchedBody);
            });
        } else if (crudApi.apiUrl == ACTION_STATUS_ATTACHMENTS) {
            const id = this.extractIdFromUrl(req.url, crudApi.apiUrl);
            if (crudApi.method == RequestMethod.POST) {
                const s3Key = `${this.OFFLINE_ID_PREFIX}_${this.generateRandomMongoObjectId()}`;
                offlineReq.id = s3Key;
                offlineReq.body = null;
                const file = body.values().toArray()[0];
                offlineReq.file = file;
                resourceUpdateRequest = this.indexedDBService.findByUniqueKey(storeName, id).then(obj => {
                    let attachments = obj.attachments || [];
                    attachments.push({ filename: file.name, s3Key: s3Key, type: ActionAttachmentType.OTHER });
                    const updatedAction = Object.assign(obj, { attachments });
                    return this.doSaveResource(storeName, updatedAction);
                });
            } else { // method == DELETE
                const s3Key = req.params.get('s3Key');
                resourceUpdateRequest = this.indexedDBService.findByUniqueKey(storeName, id).then((obj: ActionStatus) => {
                    let attachmentIndex = obj.attachments?.findIndex(a => a.s3Key == s3Key);
                    if (attachmentIndex >= 0) {
                        obj.attachments?.splice(attachmentIndex, 1);
                    }
                    return this.doSaveResource(storeName, obj);
                });
                if (s3Key.startsWith(this.OFFLINE_ID_PREFIX)) {
                    offlineSaveRequest = this.indexedDBService.clearById(IndexedDBService.OFFLINE_REQUEST_STORE_NAME, s3Key);
                }
            }
        } else if (crudApi.apiUrl == TICKET_NOTES) {
            const id = this.extractIdFromUrl(req.url, crudApi.apiUrl);
            resourceUpdateRequest = this.indexedDBService.findByUniqueKey(storeName, id).then(obj => {
                const items = obj.items || [];
                const user = this.authenticationService.getUser();
                items.push({ note: offlineReq.body, timestamp: new Date().getTime(), user: user, userId: user.id, type: TicketItemType.NOTE });
                const updatedTicket = Object.assign(obj, { items });
                return this.doSaveResource(storeName, updatedTicket);
            });
        } else if (crudApi.apiUrl == TICKET_ATTACHMENTS) {
            const id = this.extractIdFromUrl(req.url, crudApi.apiUrl);
            const s3Key = `${this.OFFLINE_ID_PREFIX}_${this.generateRandomMongoObjectId()}`;
            offlineReq.id = s3Key;
            offlineReq.body = null;
            const file = body.values().toArray()[0];
            offlineReq.file = file;
            resourceUpdateRequest = this.indexedDBService.findByUniqueKey(storeName, id).then(obj => {
                const items = obj.items || [];
                const user = this.authenticationService.getUser();
                items.push({ attachment: { filename: file.name, s3Key: s3Key, type: ActionAttachmentType.OTHER }, timestamp: new Date().getTime(), user: user, userId: user.id, type: TicketItemType.ATTACHMENT });
                const updatedTicket = Object.assign(obj, { items });
                return this.doSaveResource(storeName, updatedTicket);
            });
        } else { // simple creation POST
            resourceUpdateRequest = this.doSaveResource(storeName, body)
        }
        if (!offlineSaveRequest) {
            offlineSaveRequest = this.doSaveResource(IndexedDBService.OFFLINE_REQUEST_STORE_NAME, offlineReq)
        }
        return Promise.all([resourceUpdateRequest, offlineSaveRequest]);
    }


    private getHeadersMap(req: HttpRequest<any>): { [header: string]: string[]; } {
        const headersMap: { [header: string]: string[]; } = {};
        req.headers.keys().map(k => headersMap[k] = req.headers.getAll(k));
        return headersMap;
    }

    private getParamsMap(req: HttpRequest<any>): { [header: string]: string[]; } {
        const paramsMap: { [param: string]: string[]; } = {};
        req.params.keys().map(k => paramsMap[k] = req.params.getAll(k));
        return paramsMap;
    }

    private getBody(req: HttpRequest<any>, crudApi: OfflineStoreCrudApi): any {
        if (crudApi.addCustomId) {
            return Object.assign(req.body, { id: this.generateRandomMongoObjectId() });
        }
        return req.body;
    }

    private generateRandomMongoObjectId(): string {
        this.counter = (this.counter + 1) % 0xffffff;
        const timestamp = Math.floor(Date.now() / 1000);
        const bytes = [
            (timestamp >> 24) & 0xff,
            (timestamp >> 16) & 0xff,
            (timestamp >> 8) & 0xff,
            timestamp & 0xff,
            ...this.random,
            (this.counter >> 16) & 0xff,
            (this.counter >> 8) & 0xff,
            this.counter & 0xff,
        ];
        return bytes.map(b => b.toString(16).padStart(2, "0")).join("");
    }

    getAllThingData(): Promise<OfflineThingData[]> {
        return this.indexedDBService.findAll(IndexedDBService.THING_DATA_STORE_NAME)
    }

    getThingDataById(id: string): Promise<OfflineThingData> {
        return this.indexedDBService.findByUniqueKey(IndexedDBService.THING_DATA_STORE_NAME, id);
    }
}

class OfflineThingData {
    id: string;
    timestamp: number;
}